import {objectFromEntries} from '@benchling/js-utils';
import {css} from 'aphrodite';
import produce from 'immer';
import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';

import {assert} from 'Asserts';
import DocumentStore from 'notebook/documents/DocumentStore';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import useDraftMethodEditor, {
  useValidateDraftState,
} from 'procedures/design/method-config/useDraftMethodEditor';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationErrorType} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  filterProcedureValidationErrors,
  findParameterTableValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {
  Method,
  MethodNode,
  ProcedureDataParameterSetting,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  ProcedurePageContext,
  BlockingSaveObjectSource,
  BlockingSaveOperation,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import ProcedureDataParametersConfigTableAdapter from 'procedures/tables/ProcedureDataParametersConfigTableAdapter';
import ProcedureEquipmentTableAdapter from 'procedures/tables/ProcedureEquipmentTableAdapter';
import ProcedureMaterialInputsTableAdapter from 'procedures/tables/ProcedureMaterialInputsTableAdapter';
import ProcedureMaterialOutputsTableAdapter from 'procedures/tables/ProcedureMaterialOutputsTableAdapter';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import DataParameterConfigTable from './DataParameterConfigTable';
import EquipmentConfigTable from './EquipmentConfigTable';
import MaterialInputConfigTable from './MaterialInputConfigTable';
import MaterialOutputConfigTable from './MaterialOutputConfigTable';
import {MethodEditorTabSavingButtons, styles as sharedStyles} from './MethodEditorTabShared';
import useStartEditingLegacyDefinitions from './useStartEditingLegacyDefinitions';

export interface MethodDefinitionTablesProps {
  isEditingMethodTab: boolean;
  isProcedureReadOnly: boolean;
  editingMethodCid: string;
  selectedMethod: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  updateIsEditingState: (newState: boolean) => void;
  navigateToStep: (stepCid: string) => void;
}

type ErrorState = Record<string, boolean>;

function reduceErrors(state: ErrorState, action: {key: string; isError: boolean}): ErrorState {
  return {
    ...state,
    [action.key]: action.isError,
  };
}

function useMultiTableErrorState(): {
  anyErrors: boolean;
  setErrorState: (key: string, isError: boolean) => void;
} {
  const [errorState, setAnyErrors] = useReducer(reduceErrors, {});
  const anyErrors = Object.values(errorState).some((e) => e);

  const setErrorState = useCallback(
    (key: string, isError: boolean) => {
      if (isError !== errorState[key]) {
        setAnyErrors({key, isError});
      }
    },
    [errorState]
  );

  return {
    anyErrors,
    setErrorState,
  };
}

type TableReadyState = Record<string, boolean>;

function reduceTableReady(state: TableReadyState, action: {key: string}): TableReadyState {
  return {
    ...state,
    [action.key]: true,
  };
}
function useAreAllTablesReadyForEditing(tableKeys: Array<string>): {
  ready: boolean;
  setTableReady: (key: string) => () => void;
} {
  const [readyState, setTableReadyState] = useReducer(
    reduceTableReady,
    // All tables are initialized as "not ready"
    objectFromEntries(tableKeys.map((key) => [key, false]))
  );
  const ready = Object.values(readyState).every((e) => e);

  const setTableReady = useCallback(
    (key: string) => () => {
      assert(readyState.hasOwnProperty(key), 'Improperly prepared tableReadyState', {tableKeys, key});
      if (!readyState[key]) {
        setTableReadyState({key});
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readyState]
  );

  return {
    ready,
    setTableReady,
  };
}

/**
 * We treat empty rows as non-errors while the user is editing the table. We strip these empty rows as
 * part of the pre-save middleware
 */
const stripEmptyRowsMiddleware = stripEmptyRows<MethodNode>;

interface MethodWithParameters {
  dataParameterSettings: Array<ProcedureDataParameterSetting>;
  materialInputSettings: Array<ProcedureMaterialInputSetting>;
  materialOutputSettings: Array<ProcedureMaterialOutputSetting>;
  equipmentSettings: Array<ProcedureEquipmentSetting>;
}

function stripEmptyRows<T extends MethodWithParameters>(method: T): T {
  return produce(method, (draftMethod) => {
    // We have to filter out empty settings values before sending them to the server, as the tables allow the user
    // to create empty rows without flagging them as errors.
    draftMethod.dataParameterSettings = draftMethod.dataParameterSettings.filter(
      (p) => !ProcedureDataParametersConfigTableAdapter.isDataParameterSettingEmpty(p)
    );
    draftMethod.equipmentSettings = draftMethod.equipmentSettings.filter(
      (e) => !ProcedureEquipmentTableAdapter.isEquipmentSettingEmpty(e)
    );
    draftMethod.materialInputSettings = draftMethod.materialInputSettings.filter(
      (mi) => !ProcedureMaterialInputsTableAdapter.isMaterialInputEmpty(mi)
    );
    draftMethod.materialOutputSettings = draftMethod.materialOutputSettings.filter(
      (mo) => !ProcedureMaterialOutputsTableAdapter.isMaterialOutputEmpty(mo)
    );
  });
}

export default function MethodDefinitionTablesWrapper(props: MethodDefinitionTablesProps): JSX.Element {
  const {saveImmediately} = MethodSidepanelContext.useContext();
  return saveImmediately == null ? (
    <MethodDefinitionTablesLegacy {...props} />
  ) : (
    <MethodDefinitionTablesPowWrapper {...props} />
  );
}

function MethodDefinitionTablesLegacy({
  isEditingMethodTab,
  isProcedureReadOnly,
  editingMethodCid,
  selectedMethod,
  onChangeMethod,
  updateIsEditingState,
  navigateToStep,
}: MethodDefinitionTablesProps): JSX.Element {
  const {
    legacySaveImmediately,
    resetLegacyDraftFlowchartChanges,
    registerLegacyPreSaveImmediatelyMiddleware,
    unregisterLegacyPreSaveImmediatelyMiddleware,
    procedureMetadata,
    legacyMaterialFlows,
  } = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const procedureId = procedureMetadata.id;
  const [middlewareId, setMiddlewareId] = useState<string | null>(null);

  // Use useValidateDraftState for validation consistency with PoW. During edits, this overrides the
  // errors to show only those associated with the method being edited.
  const draftMethod = isEditingMethodTab ? selectedMethod : null;
  const newValidationContextValue = useValidateDraftState({draftMethod, procedureType});
  const {procedureValidationErrors, setAnyTabValidationErrors} = newValidationContextValue;
  const relevantProcedureValidationErrors = findParameterTableValidationErrors(
    procedureValidationErrors,
    editingMethodCid
  );
  const productLimitErrors = filterProcedureValidationErrors(procedureValidationErrors, {
    errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
  });
  const [tableRebuildKey, setTableRebuildKey] = useState(0);
  const {anyErrors: _anyTableErrors, setErrorState: setAnyTableErrors} = useMultiTableErrorState();
  const anyErrors = _anyTableErrors || relevantProcedureValidationErrors.length > 0;
  const {ready: areTablesReadyForEditing, setTableReady} = useAreAllTablesReadyForEditing([
    'mInput',
    'mOutput',
    'dParam',
    'equip',
  ]);

  // This component is the source of truth for table errors (i.e., native table errors that are not surface in
  // our validateProcedure suite). We pass that information up so that other components have access.
  useEffect(() => {
    setAnyTabValidationErrors(_anyTableErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_anyTableErrors]);

  // On unmount, we ensure that the error status is reset, and any middleware is removed
  useEffect(() => {
    setAnyTabValidationErrors(false);
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register the middleware for removing empty rows in parameter tables
  useEffect(() => {
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    if (isEditingMethodTab) {
      const newMiddlewareId = registerLegacyPreSaveImmediatelyMiddleware(
        selectedMethod.cid,
        stripEmptyRowsMiddleware
      );
      setMiddlewareId(newMiddlewareId);
    } else {
      setMiddlewareId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMethodTab, selectedMethod.cid]);

  const {handleOnStartEditing, editingState} = useStartEditingLegacyDefinitions({
    isEditingMethodTab,
    procedureId,
    updateIsEditingState,
  });

  const shouldBlockEditingDefinitions = !isEditingMethodTab || isProcedureReadOnly;
  const errorText =
    productLimitErrors.length > 0
      ? `Definitions can't be submitted because the number of definitions in your ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} exceeds ${PROCEDURE_PRODUCT_LIMITS.PARAMETERS_PER_PROCEDURE}`
      : 'Please fix errors in the tables below';

  return (
    <ProcedureValidationContext.Provider value={newValidationContextValue}>
      <div
        {...getTestProps({component: 'MethodDefinitionTables', element: 'root'})}
        className={css(sharedStyles.detailsContainer)}
      >
        <div className={css(sharedStyles.detailsHeader)}>
          <div>
            <FieldLabel label={MethodEditorTab.DEFINITIONS} />
            <p>
              Model your material inputs, material outputs, parameters, and equipment to capture data against.
            </p>
          </div>
          {!isProcedureReadOnly && (
            <MethodEditorTabSavingButtons
              editingState={editingState}
              anyErrors={anyErrors}
              errorText={errorText}
              areTablesStillLoading={!areTablesReadyForEditing}
              handleOnCancel={() => {
                resetLegacyDraftFlowchartChanges();
                updateIsEditingState(false);
              }}
              handleOnSubmit={async () => {
                // This call requires an immediate API call, with an overlay. The middleware (above) handles cleanup tasks
                await legacySaveImmediately(
                  () => ({
                    blockingSaveObjectSource: BlockingSaveObjectSource.Parameter,
                    blockingSaveOperation: BlockingSaveOperation.Update,
                  }),
                  'Saving definitions...'
                );
                // When saving Parameter Definitions, we need to clear the saved documents in memory so
                // that the user fetches them with any new changes applied
                DocumentStore.resetState();
                updateIsEditingState(false);
                setTableRebuildKey((k) => k + 1);
              }}
              handleOnEdit={handleOnStartEditing}
            />
          )}
        </div>
        <div className={css(sharedStyles.detailsContent)}>
          <div className={css(sharedStyles.detailsContentScrollContainer)}>
            <MaterialInputConfigTable
              key={`mi${tableRebuildKey}`}
              method={selectedMethod}
              onChangeMethod={onChangeMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('mInput', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('mInput')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
              materialFlows={legacyMaterialFlows}
            />
            <MaterialOutputConfigTable
              key={`mo${tableRebuildKey}`}
              method={selectedMethod}
              onChangeMethod={onChangeMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('mOutput', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('mOutput')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
              materialFlows={legacyMaterialFlows}
            />
            <DataParameterConfigTable
              key={`dp${tableRebuildKey}`}
              method={selectedMethod}
              onChangeMethod={onChangeMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('dParam', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('dParam')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
            />
            <EquipmentConfigTable
              key={`eq${tableRebuildKey}`}
              method={selectedMethod}
              onChangeMethod={onChangeMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('equip', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('equip')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
            />
          </div>
        </div>
      </div>
    </ProcedureValidationContext.Provider>
  );
}

/**
  This wrapper exists to ensure that the tables are all rebuilt after a save
*/
function MethodDefinitionTablesPowWrapper(props: MethodDefinitionTablesProps): JSX.Element {
  const [rebuildKey, setRebuildKey] = useState(0);
  const contextValue = MethodSidepanelContext.useContext();

  // When we save, ensure that the components are rebuilt wth fresh data
  const wrappedSave = useCallback(
    async (methodData: Method) => {
      await contextValue.saveImmediately!(methodData);
      setRebuildKey((k) => k + 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- oxlint incorrectly reports both missing contextValue and unnecessary contextValue.saveImmediately
    [contextValue.saveImmediately]
  );
  const wrappedContextValue = useMemo(
    () => ({
      ...contextValue,
      saveImmediately: wrappedSave,
    }),
    [contextValue, wrappedSave]
  );

  return (
    <MethodSidepanelContext.Provider value={wrappedContextValue}>
      <MethodDefinitionTablesPoW {...props} key={rebuildKey} />
    </MethodSidepanelContext.Provider>
  );
}

// The two omitted props were used in legacy procedures to manage what can now be done locally. The "lose unsaved
// changes?" modal was owned by ProcedurePage, and these props were passed down on context. Now that modal is
// owned by the workflows platform. See useDraftMethodEditor for more details.
type MethodDefinitionTablesPoWProps = Omit<
  MethodDefinitionTablesProps,
  'isEditingMethodTab' | 'updateIsEditingState'
>;

function MethodDefinitionTablesPoW({
  isProcedureReadOnly,
  editingMethodCid,
  selectedMethod,
  onChangeMethod: _onChangeMethod,
  navigateToStep,
}: MethodDefinitionTablesPoWProps): JSX.Element {
  const {procedureMetadata} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const {saveImmediately, materialFlows} = MethodSidepanelContext.useContext();
  assert(saveImmediately != null, 'MethodDefinitionTablesPoW can only run in a PoW context');

  const {anyErrors: _anyTableErrors, setErrorState: setAnyTableErrors} = useMultiTableErrorState();
  const {ready: areTablesReadyForEditing, setTableReady} = useAreAllTablesReadyForEditing([
    'mInput',
    'mOutput',
    'dParam',
    'equip',
  ]);

  // This is only called when the user explicitly presses the Save buton. It's further wrapped by
  // the useDraftMethodEditor hook below.
  const _onSave = useCallback(
    async (method: Method) => {
      await saveImmediately(stripEmptyRows(method));
      // When saving Parameter Definitions, we need to clear the saved documents in memory so
      // that the user fetches them with any new changes applied
      DocumentStore.resetState();
    },
    [saveImmediately]
  );

  const {
    isEditing,
    editingState,
    onStartEditing,
    onSave,
    onCancel,
    onUpdateMethod,
    methodDuringEditing,
    newValidationContextValue,
  } = useDraftMethodEditor({
    method: selectedMethod,
    onSave: _onSave,
    onChangeMethod: _onChangeMethod,
    pauseCondition: ProcedurePauseCondition.IS_EDITING_PARAMETERS,
    procedureType,
  });

  // The true error state is the combination of [native table errors] + [procedure validation errors]
  //  (And note that we're getting the main list of [procedure validation errors] from the draft editor.)
  const {procedureValidationErrors, setAnyTabValidationErrors} = newValidationContextValue;
  const relevantProcedureValidationErrors = findParameterTableValidationErrors(
    procedureValidationErrors,
    editingMethodCid
  );
  const anyErrors = _anyTableErrors || relevantProcedureValidationErrors.length > 0;
  const productLimitErrors = filterProcedureValidationErrors(procedureValidationErrors, {
    errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
  });

  // This component is the source of truth for table errors (i.e., native table errors that are not surfaced in
  // our validateProcedure suite). We pass that information up so that other components have access.
  useEffect(() => {
    setAnyTabValidationErrors(_anyTableErrors);
  }, [setAnyTabValidationErrors, _anyTableErrors]);

  // On unmount, we ensure that the error status is reset, and any middleware is removed
  useEffect(() => {
    setAnyTabValidationErrors(false);
  }, [setAnyTabValidationErrors]);

  const shouldBlockEditingDefinitions = !isEditing || isProcedureReadOnly;
  const errorText =
    productLimitErrors.length > 0
      ? `Definitions can't be submitted because the number of definitions in your ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} exceeds ${PROCEDURE_PRODUCT_LIMITS.PARAMETERS_PER_PROCEDURE}`
      : 'Please fix errors in the tables below';

  return (
    <ProcedureValidationContext.Provider value={newValidationContextValue}>
      <div
        {...getTestProps({component: 'MethodDefinitionTables', element: 'root'})}
        className={css(sharedStyles.detailsContainer)}
      >
        <div className={css(sharedStyles.detailsHeader)}>
          <div>
            <FieldLabel label={MethodEditorTab.DEFINITIONS} />
            <p>
              Model your material inputs, material outputs, parameters, and equipment to capture data against.
            </p>
          </div>
          {!isProcedureReadOnly && (
            <MethodEditorTabSavingButtons
              editingState={editingState}
              anyErrors={anyErrors}
              errorText={errorText}
              areTablesStillLoading={!areTablesReadyForEditing}
              handleOnCancel={onCancel}
              handleOnSubmit={onSave}
              handleOnEdit={onStartEditing}
            />
          )}
        </div>
        <div className={css(sharedStyles.detailsContent)}>
          <div className={css(sharedStyles.detailsContentScrollContainer)}>
            <MaterialInputConfigTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('mInput', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('mInput')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
              materialFlows={materialFlows}
            />
            <MaterialOutputConfigTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('mOutput', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('mOutput')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
              materialFlows={materialFlows}
            />
            <DataParameterConfigTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('dParam', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('dParam')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
            />
            <EquipmentConfigTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              onChangeErrorState={(tableHasErrors) => {
                setAnyTableErrors('equip', tableHasErrors);
              }}
              onReadyForEditing={setTableReady('equip')}
              locked={shouldBlockEditingDefinitions}
              navigateToStep={navigateToStep}
            />
          </div>
        </div>
      </div>
    </ProcedureValidationContext.Provider>
  );
}
